import React from 'react';
import s from './CartItemThumbnail.scss';
import {ItemLink} from '../CartItem/partials/ItemLink/ItemLink';
import classNames from 'clsx';
import {LineItemModel} from '../../../../domain/models/LineItems/LineItem.model';
import {ThumbnailImage} from '@wix/wix-ui-tpa/cssVars';
import {getImageDimensions} from '../../../../domain/utils/itemUtilsWithSideEffect';
import {ProductType} from '@wix/wixstores-client-core';
import {useExperiments} from '@wix/yoshi-flow-editor';
import {SPECS} from '../../../../domain/specs';
import {Download, Document, GiftSmall} from '@wix/wix-ui-icons-common/on-stage';

export enum CartItemThumbnailDataHook {
  Image = 'CartItemThumbnailDataHook.Image',
  DefaultImage = 'default-image',
  DefaultImageDigital = 'default-image-digital',
  DefaultImagePhysical = 'default-image-physical',
  Badge = 'product-badge',
  Wrapper = 'product-thumbnail-wrapper',
  Media = 'product-thumbnail-media',
  QuantityBadge = 'CartItemThumbnailDataHook.quantityBadge',
}

const DEFAULT_IMAGE_DEFAULT_SIZE = 50;

export const CartItemThumbnail = ({
  lineItem,
  className,
  quantityBadge,
}: {
  lineItem: LineItemModel;
  className?: string;
  quantityBadge?: number;
}) => {
  const {experiments} = useExperiments();
  const imageDimensions = getImageDimensions(
    lineItem,
    CartItemThumbnail.defaultDimensions,
    experiments.enabled(SPECS.FixImageDimensionsOnCart)
  );

  const renderImage = () => {
    if (!lineItem.attributes.image) {
      return (
        <div
          data-hook={CartItemThumbnailDataHook.DefaultImage}
          className={classNames(s.defaultImage)}
          style={{
            width: imageDimensions.width,
            height: imageDimensions.height,
          }}>
          {lineItem.attributes.itemType === ProductType.DIGITAL ? (
            <Document
              size={DEFAULT_IMAGE_DEFAULT_SIZE.toString()}
              data-hook={CartItemThumbnailDataHook.DefaultImageDigital}
            />
          ) : (
            <GiftSmall
              size={DEFAULT_IMAGE_DEFAULT_SIZE.toString()}
              data-hook={CartItemThumbnailDataHook.DefaultImagePhysical}
            />
          )}
        </div>
      );
    }

    return (
      <div className={classNames(s.productImage)} data-hook={CartItemThumbnailDataHook.Wrapper}>
        <ThumbnailImage
          src={lineItem.attributes.image.id ?? /* istanbul ignore next */ lineItem.attributes.image.url}
          width={imageDimensions.width}
          height={imageDimensions.height}
          alt={lineItem.name}
          data-hook={CartItemThumbnailDataHook.Media}
          aria-hidden={false}
        />
        {lineItem.attributes.itemType === ProductType.DIGITAL && (
          <div className={classNames(s.productBadge)} data-hook={CartItemThumbnailDataHook.Badge}>
            <Download />
          </div>
        )}
      </div>
    );
  };

  const isQuantityBadgeEnabled = experiments.enabled(SPECS.CartItemQuantityBadge);

  if (!isQuantityBadgeEnabled) {
    return (
      <div className={classNames(s.thumbnailOld, className)} data-hook={CartItemThumbnailDataHook.Image}>
        <ItemLink lineItem={lineItem} tabIndex={-1}>
          {renderImage()}
        </ItemLink>
      </div>
    );
  }

  return (
    <div className={classNames(s.thumbnail, className)} data-hook={CartItemThumbnailDataHook.Image}>
      <div className={s.thumbnailContent}>
        <ItemLink lineItem={lineItem} tabIndex={-1}>
          {renderImage()}
        </ItemLink>
      </div>
      {quantityBadge !== undefined && (
        <span data-hook={CartItemThumbnailDataHook.QuantityBadge} className={s.quantityBadge} aria-hidden="true">
          {quantityBadge}
        </span>
      )}
    </div>
  );
};

CartItemThumbnail.defaultDimensions = {width: 100, height: 100};
